const { debounce } = window.BAO.Utils
const preBuyUtils = new window.PreBuyUtils()
class CartItem extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['remove']
  }

  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.removeLineItem = this.removeLineItem.bind(this)
    this.changeLineItem = this.changeLineItem.bind(this)
    this.changeCartLineItem = this.changeCartLineItem.bind(this)
    this.removeWishlistLineItem = this.removeWishlistLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.debouncedOnQuantityChange = debounce(event => {
      this.onQuantityChange(event)
    }, 500)

    if (this.els?.remove.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    if (this.els?.variant?.exists) {
      this.listeners.add(
        this.els.variant.element,
        'change',
        this.changeLineItem,
      )
    }

    if (this.els?.cartvariant?.exists) {
      this.listeners.add(
        this.els.cartvariant.element,
        'change',
        this.changeCartLineItem,
      )
    }

    if (this.els?.wishlist?.exists) {
      this.listeners.add(
        this.els.wishlist.element,
        'click',
        this.removeWishlistLineItem,
      )
    }

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )
  }

  async removeLineItem (e) {
    e?.preventDefault()

    this.loading = true

    const state = await window.BAO.Cart.removeItem(this.dataset.key, {
      sections: this.mappedSections,
    })

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)
  }

  /**
   * @param {MouseEvent} event
   *
   * @returns {void}
   */
  async changeLineItem (event) {
    this.loading = true
    this.setAttribute('aria-busy', 'true')

    try {
      const id = event.target.value
      const option =
        event?.target?.options[event?.target?.selectedIndex]?.dataset
      const properties = {}

      let quantity = 1
      const maxQuantity =
        event?.target?.options[event?.target?.selectedIndex]?.dataset
          ?.maxInventory ?? 1
      const currentQuantity = event?.target?.dataset?.currentQuantity ?? 1

      const preBuyData = await preBuyUtils.getPreBuyData(
        option.productId,
        event.target.value,
      )

      // eslint-disable-next-line camelcase
      let selling_plan = ''
      if (preBuyData?.enabled && preBuyData?.variantPool) {
        // eslint-disable-next-line camelcase
        selling_plan =
          preBuyData?.variantPool?.sellingPlanId?.replace(
            'gid://shopify/SellingPlan/',
            '',
          ) || ''
        properties._preorder = 'true'
        properties[
          window.theme?.strings?.estimatedDispatchPrefix || 'Pre-order'
        ] = preBuyData?.variantPool?.estDispatchDateFormatted
        properties._pool_location = preBuyData?.variantPool?.location
        properties._locationId = preBuyData?.variantPool?.locationId
        const date = new Date(preBuyData?.variantPool?.zonedEstDispatchDate)
        properties._dispatch_date = preBuyData.variantPool.zonedEstDispatchDate
        const month = date.getMonth() + 1
        properties._start_date = `${String(month).padStart(2, '0')}/${String(
          date.getDate(),
        ).padStart(2, '0')}/${date.getFullYear()}`
      } else {
        const preOrderKey = window?.theme?.strings?.estimatedDispatchPrefix
        const preOrderProperty =
          event?.target?.options[event?.target?.selectedIndex]?.dataset
            ?.preorder
        if (preOrderProperty !== undefined) {
          properties[preOrderKey] = preOrderProperty
          properties._preorder = true
        }

        quantity = Math.min(currentQuantity, maxQuantity)
      }

      await this.removeLineItem()
      const state = await window.BAO.Cart.addItem(
        // eslint-disable-next-line camelcase
        { id, quantity, selling_plan, properties },
        {
          sections: this.mappedSections,
        },
      )

      this.loading = false

      if ('sections' in state) this.renderSections(state.sections)
    } catch (error) {
      console.error('Error adding variant:', error)
    }
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async changeCartLineItem (e) {
    const id = e.target.value
    this.setAttribute('aria-busy', 'true')
    let properties = null
    const preOrderKey = window.theme.strings.estimatedDispatchPrefix
    const preOrderProperty =
      e.target.options[e.target.selectedIndex].dataset.preorder
    if (preOrderProperty !== undefined) {
      properties = { [preOrderKey]: preOrderProperty, _preorder: true }
    }

    this.loading = true

    await window.BAO.Cart.updateQuantity(this.dataset.key, 0, {
      sections: this.mappedSections,
    })

    const item = {
      id,
      quantity: 1,
      properties,
    }
    const state = await window.BAO.Cart.addItemFromCart(item)

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async removeWishlistLineItem (e) {
    e?.preventDefault()

    if (e.currentTarget.classList.contains('iwishAdded')) return

    this.loading = true

    const state = await window.BAO.Cart.removeItem(this.dataset.key, {
      sections: this.mappedSections,
    })

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)

    if (typeof Storage !== 'undefined') {
      document
        .querySelectorAll('[data-i-wishlist-external-el="count"]')
        .forEach(countEl => {
          const count =
            typeof localStorage.iWishCnt !== 'undefined'
              ? localStorage.iWishCnt
              : ''
          countEl.dataset.count = count
          countEl.innerHTML = count
        })
    }
  }

  async onQuantityChange (e) {
    this.loading = true

    const state = await window.BAO.Cart.updateQuantity(
      this.dataset.key,
      e.detail.value,
      { sections: this.mappedSections },
    )

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)
  }

  get mappedSections () {
    return this.sections.map(s => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
